import { Card } from "@/components/ui/Card";
import { StatCard } from "@/components/ui/StatCard";
import { TimeSeriesChart } from "@/components/ui/TimeSeriesChart";
import { LoadingState, ErrorState, DemoDataNotice } from "@/components/ui/AsyncState";
import { useApi } from "@/lib/use-api";
import { formatCurrencyFromCents } from "@/lib/format";
import { RangePicker, rangeQuery, type RangeSelection } from "@/components/ui/RangePicker";
import { formatPercent } from "@/lib/format";
import { useState } from "react";
import type { PlatformAnalytics, PlatformActivityAnalytics } from "@platform/shared";

export function AdminAnalytics() {
  const { data, isLoading, error, refetch } = useApi<{ analytics: PlatformAnalytics }>("/api/admin/analytics");
  const [selection, setSelection] = useState<RangeSelection>({ range: "30d" });
  const query = rangeQuery(selection);
  const activity = useApi<{ activity: PlatformActivityAnalytics }>(`/api/admin/analytics/activity?${query}`, [query]);
  const act = activity.data?.activity;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Platform analytics</h1>
        <p className="mt-1 text-sm text-ink-soft">Usage trends across all customer organizations.</p>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg text-ink">Customer activity &amp; ROI</h2>
          <p className="mt-1 text-sm text-ink-soft">
            Summed across all organizations (aggregate only).
            {act && ` ${act.startDate} – ${act.endDate} UTC · ${act.organizationsWithActivity} active organizations.`}
          </p>
        </div>
        <RangePicker value={selection} onChange={setSelection} />
      </div>
      {activity.isLoading && <LoadingState label="Loading activity…" />}
      {activity.error && <ErrorState message={activity.error} onRetry={activity.refetch} />}
      {act && (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <StatCard label="Total calls" value={String(act.totals.totalCalls)} />
            <StatCard label="Missed calls" value={String(act.totals.missedCalls)} hint={`${act.totals.missedCallsFollowedUp} followed up`} />
            <StatCard label="SMS sent / received" value={`${act.totals.smsSent} / ${act.totals.smsReceived}`} />
            <StatCard label="AI conversations" value={String(act.totals.aiConversations)} />
            <StatCard label="Leads created" value={String(act.totals.leadsCreated)} />
            <StatCard label="Qualified leads" value={String(act.totals.qualifiedLeads)} hint={`${formatPercent(act.qualificationRate)} qualification rate`} />
            <StatCard label="Won leads" value={String(act.totals.convertedLeads)} />
            <StatCard label="Response rate" value={formatPercent(act.responseRate)} />
            <StatCard label="Confirmed customer revenue" value={formatCurrencyFromCents(act.totals.confirmedRevenueCents)} hint="Actual, recorded by customers" />
            <StatCard label="Estimated lead value" value={formatCurrencyFromCents(act.totals.estimatedLeadValueCents)} hint="Projection — not revenue" />
          </div>
          {act.organizationsPending > 0 && (
            <p className="text-xs text-ink-soft">
              {act.organizationsPending} organization(s) not yet refreshed for this range; totals may be slightly low until the hourly job runs.
            </p>
          )}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Calls</p>
              <TimeSeriesChart points={act.daily.map((p) => ({ label: p.date.slice(5), value: p.totalCalls }))} color="#B45309" />
            </Card>
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Confirmed customer revenue</p>
              <TimeSeriesChart
                points={act.daily.map((p) => ({ label: p.date.slice(5), value: p.confirmedRevenueCents }))}
                formatValue={formatCurrencyFromCents}
                color="#166534"
              />
            </Card>
          </div>
        </>
      )}

      <h2 className="pt-2 font-display text-lg text-ink">Platform growth</h2>
      {isLoading && <LoadingState label="Loading platform analytics…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Organizations" value={String(data.analytics.systemUsage.totalOrganizations)} />
            <StatCard label="Users" value={String(data.analytics.systemUsage.totalUsers)} />
            <StatCard label="Leads" value={String(data.analytics.systemUsage.totalLeads)} />
            <StatCard label="Calls" value={String(data.analytics.systemUsage.totalCalls)} />
            <StatCard label="Conversations" value={String(data.analytics.systemUsage.totalConversations)} />
            <StatCard label="Messages" value={String(data.analytics.systemUsage.totalMessages)} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Customer growth ({data.analytics.rangeDays}d)</p>
              <TimeSeriesChart
                points={data.analytics.customerGrowth.map((p) => ({ label: p.date.slice(5), value: p.totalCustomers }))}
              />
            </Card>
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Revenue growth ({data.analytics.rangeDays}d)</p>
              <TimeSeriesChart
                points={data.analytics.revenueGrowth.map((p) => ({ label: p.date.slice(5), value: p.revenueCents }))}
                formatValue={formatCurrencyFromCents}
                color="#166534"
              />
            </Card>
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Lead volume ({data.analytics.rangeDays}d)</p>
              <TimeSeriesChart
                points={data.analytics.leadVolume.map((p) => ({ label: p.date.slice(5), value: p.count }))}
                color="#7C3AED"
              />
            </Card>
            <Card className="p-5">
              <p className="mb-4 font-medium text-ink">Calls processed ({data.analytics.rangeDays}d)</p>
              <TimeSeriesChart
                points={data.analytics.callsProcessed.map((p) => ({ label: p.date.slice(5), value: p.count }))}
                color="#B45309"
              />
            </Card>
            <Card className="p-5 lg:col-span-2">
              <p className="mb-4 font-medium text-ink">AI conversations ({data.analytics.rangeDays}d)</p>
              <TimeSeriesChart
                points={data.analytics.aiConversations.map((p) => ({ label: p.date.slice(5), value: p.count }))}
                color="#0891B2"
              />
            </Card>
          </div>

          <DemoDataNotice>
            Every chart and total here is a live aggregate query over `leads`, `calls`, `conversations`,
            `organizations`, and `platform_revenue`. Activity volume is currently limited to seeded or manually
            entered data — the telephony webhook and SMS/AI conversation engine that will generate real activity
            are future steps.
          </DemoDataNotice>
        </>
      )}
    </div>
  );
}
